import { Component, inject, effect, signal, WritableSignal } from "@angular/core";
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from "@angular/router";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";

import { FieldsetModule } from "primeng/fieldset";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { IftaLabelModule } from "primeng/iftalabel";
import { TextareaModule } from "primeng/textarea";
import { ToastService } from '../../../../common/services/toast.service';
import { ShellCoursesService } from "../../services/shell-courses.service";
import { LessonsToolsMappingService } from "../../../deeplink/services/lessons-tools-mapping.service";
import { NgClass } from "@angular/common";
import { MessageModule } from "primeng/message";
import { FloatLabelModule } from 'primeng/floatlabel';

@Component({
  selector: 'app-add-shell-course-and-lessons',
  standalone: true,
  imports: [
    FieldsetModule,
    ButtonModule,
    InputTextModule,
    IftaLabelModule,
    TextareaModule,
    ReactiveFormsModule,
    RouterModule,
    NgClass,
    MessageModule,
    FloatLabelModule
  ],
  providers: [LessonsToolsMappingService, ShellCoursesService],
  templateUrl: './add-shell-courses.html',
  styles: [`
    .pulsate {
      animation: pulsate 1s ease-in-out 3;
    }
    @keyframes pulsate {
      0%, 100% { opacity: 1; transform: scale(1); }
      50% { opacity: 0; transform: scale(1); }
    }
  `]
})
export class AddShellCoursesComponent {
  private fb = inject(FormBuilder);
  private shellCoursesService = inject(ShellCoursesService);
  private toastService = inject(ToastService)
  router = inject(Router);
  wasCheckAvailabilityButtonClicked = signal<boolean>(false)
  form: FormGroup = this.fb.group({
    codeCommon:  ['SHELL-COURSE-CODE', [Validators.required, Validators.minLength(3), Validators.maxLength(100)]],
    numberRangeFrom: [1, [Validators.required, Validators.minLength(1), Validators.maxLength(100)]],
    numberRangeTo: [10, [Validators.required,Validators.minLength(1), Validators.maxLength(100)]],
    zeroPadding: [2, [Validators.min(0), Validators.max(10), Validators.minLength(1), Validators.maxLength(2)]],
  });

  private formValues = toSignal(this.form.valueChanges, { initialValue: this.form.value });

  constructor() {
    effect(() => {
      this.formValues(); // track signal — runs whenever any control changes
      this.showPreview();
    });
  }
 
  shellCoursesPreview = signal<string>('');
  presentCourseCodes = signal<string>('');
  absentCourseCodes = signal<string>('');

  showPreview() {
    const { codeCommon, numberRangeFrom, numberRangeTo, zeroPadding: padLength } = this.form.value;
    if (!codeCommon || numberRangeFrom == null || numberRangeTo == null) {
      this.shellCoursesPreview.set('');
      return;
    }
    const shellCoursesLines: string[] = [];
    for (let i = Number(numberRangeFrom); i <= Number(numberRangeTo); i++) {
      const paddedNumber = String(i).padStart(Number(padLength), '0');
      shellCoursesLines.push(`${codeCommon}-${paddedNumber}`)
    }
    this.shellCoursesPreview.set(shellCoursesLines.join(',\n'));
  }
  async checkAvailability(){
    try{
      const courseCodes = this.shellCoursesPreview().split(",\n");
      const availableCourseCodesResult = await this.shellCoursesService.checkAvailabilityFor({courseCodes});
      this.showOnUI(this.presentCourseCodes, availableCourseCodesResult.present)
      this.showOnUI(this.absentCourseCodes, availableCourseCodesResult.absent)
      this.wasCheckAvailabilityButtonClicked.set(true);
    }catch(e){
      this.toastService.show({ severity: 'error', summary: 'Error', detail: 'There was a problem please contact tech support.' });
    }
    
  }
  private showOnUI(signalObject:WritableSignal<string>, data:string[]){
     const lines: string[] = [];
     for (let i = 0; i < data.length; i++) {
      lines.push(data[i]);
    }
    signalObject.set(lines.join(`,<br>`));
  }
  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.show({ severity: 'error', summary: 'Error', detail: 'Please correct the errors in the form.' });
      return;
    }
    try {
      const courseCodes = this.absentCourseCodes().split(`,<br>`);
      await this.shellCoursesService.createShellCourses({courseCodes});
      this.toastService.show({ severity: 'success', summary: 'Success', detail: 'Shell course created successfully.' });
      this.router.navigate(['/view-shell-courses']);
    } catch {
      this.toastService.show({ severity: 'error', summary: 'Error', detail: 'Failed to create shell course. Please try again.' });
    } finally {
      this.wasCheckAvailabilityButtonClicked.set(false);
    }
  }
}
